// src/views/InvoiceEditor.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './InvoiceEditor.css';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaTimes } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

// Helpers to keep rate (amt) in DOLLARS, not cents
const toDollarInput = (s) => {
  if (s === '' || s === null || s === undefined) return '';
  // Allow '-', '.', '0.' during typing
  const n = Number(s);
  return Number.isFinite(n) ? s : '';
};
const toPretty2 = (s) => {
  const n = Number(s);
  return Number.isFinite(n) ? String(Number(n.toFixed(2))) : '';
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function getUserFullName() {
  try {
    const u =
      JSON.parse(localStorage.getItem('currentUser')) ||
      JSON.parse(localStorage.getItem('user')) ||
      {};
    return u.fullname || u.userFullName || u.name || '';
  } catch {
    return '';
  }
}

function getUserId() {
  try {
    const u =
      JSON.parse(localStorage.getItem('currentUser')) ||
      JSON.parse(localStorage.getItem('user')) ||
      {};
    const id = u.userid ?? u.userId ?? u.id;
    const n = Number(id);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

const emptyLineItem = () => ({
  id: null,
  qty: '',
  amt: '',
  chargename: '',
  description: '',
  chargeitemid: '',
  warehouseid: '',
});

const InvoiceEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // if present, we are editing

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // lookups
  const [clients, setClients] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // header
  const [invoiceId, setInvoiceId] = useState(id || null);
  const [clientid, setClientid] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  // Optional associations (map invoice to WR/Order/E-Order)
  const [assocType, setAssocType] = useState('receipt'); // receipt|order|eorder
  const [assocReceipts, setAssocReceipts] = useState([]); // number[]
  const [assocOrders, setAssocOrders] = useState([]); // number[]
  const [assocEorders, setAssocEorders] = useState([]); // number[]
  const [assocRows, setAssocRows] = useState([]); // fetched rows for current type
  const [assocSearch, setAssocSearch] = useState('');
  const [assocLoading, setAssocLoading] = useState(false);

  // line items
  const [items, setItems] = useState([emptyLineItem()]);

  // taxes & discount
  const [taxMode, setTaxMode] = useState('percent'); // percent|fixed
  const [taxValue, setTaxValue] = useState('0');
  const [discountMode, setDiscountMode] = useState('fixed'); // percent|fixed
  const [discountValue, setDiscountValue] = useState('0');

  const isEdit = !!invoiceId;

  // load lookups
  useEffect(() => {
    (async () => {
      try {
        const [clientsRes, whRes] = await Promise.all([
          fetch(`${API}/api/clients/minimal`),
          fetch(`${API}/api/warehouses`),
        ]);
        const clientsJson = await clientsRes.json();
        const whJson = await whRes.json();

        setClients(Array.isArray(clientsJson) ? clientsJson : []);
        setWarehouses(Array.isArray(whJson) ? whJson : []);
      } catch (e) {
        console.error('Failed to load lookups', e);
      }
    })();
  }, []);

  // load invoice if editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/invoices/${invoiceId}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to load invoice');
        }
        const json = await res.json();
        const { header, items: loaded } = json;

        setClientid(header.clientid);
        setDate(header.date ? header.date.slice(0, 10) : new Date().toISOString().slice(0, 10));
        setNotes(header.notes || '');

        // Pre-fill associations if present in response
        try {
          const rIds = Array.isArray(json.receiptIds) ? json.receiptIds.map(Number).filter(n => Number.isFinite(n) && n > 0) : [];
          const oIds = Array.isArray(json.orderIds)   ? json.orderIds.map(Number).filter(n => Number.isFinite(n) && n > 0)   : [];
          const eIds = Array.isArray(json.eorderIds)  ? json.eorderIds.map(Number).filter(n => Number.isFinite(n) && n > 0)  : [];
          setAssocReceipts(rIds);
          setAssocOrders(oIds);
          setAssocEorders(eIds);
        } catch {}

        // keep ID for update
        setItems(
          loaded.length
            ? loaded.map(li => ({
                id: li.id,
                qty: li.qty != null ? String(li.qty) : '',
                amt: li.amt != null ? toPretty2(li.amt) : '',
                chargename: li.chargename,
                description: li.description || '',
                chargeitemid: li.chargeitemid || '',
                warehouseid: li.warehouseid || '',
              }))
            : [emptyLineItem()]
        );
      } catch (e) {
        console.error(e);
        alert(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, invoiceId]);

  const addItem = () => {
    setItems((prev) => [...prev, emptyLineItem()]);
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const subTotal = useMemo(() => {
    return items.reduce((sum, li) => {
      const qty = Number(li.qty) || 0;
      const amt = Number(li.amt) || 0;
      return round2(sum + round2(qty * amt));
    }, 0);
  }, [items]);

  const taxAmount = useMemo(() => {
    const v = Number(taxValue) || 0;
    if (taxMode === 'percent') {
      return subTotal * (v / 100);
    }
    return v;
  }, [taxMode, taxValue, subTotal]);

  const discountAmount = useMemo(() => {
    const v = Number(discountValue) || 0;
    if (discountMode === 'percent') {
      return subTotal * (v / 100);
    }
    return v;
  }, [discountMode, discountValue, subTotal]);

  const total = useMemo(() => {
    return subTotal + taxAmount - discountAmount;
  }, [subTotal, taxAmount, discountAmount]);

  const canSave = useMemo(() => {
    if (!clientid) return false;
    const hasValid = items.some(li => Number(li.qty) > 0);
    return hasValid;
  }, [clientid, items]);

  const payloadFromState = () => ({
    clientid: Number(clientid),
    date: `${date} 00:00:00`,
    notes: notes || null,
    userFullName: getUserFullName(),
    userid: getUserId(),
    // New invoices created via this editor are "Custom"
    // (type id 5 per invoicetypes)
    type: 5,
    // Associate with selected documents
    receiptIds: assocReceipts,
    orderIds: assocOrders,
    eorderIds: assocEorders,
    tax: {
      mode: taxMode,
      value: Number(taxValue) || 0,
    },
    discount: {
      mode: discountMode,
      value: Number(discountValue) || 0,
    },
    items: items
      .filter(li => (Number(li.qty) || 0) > 0)
      .map(li => ({
        id: li.id || undefined,
        qty: Number(li.qty) || 0,
        amt: Number(toPretty2(li.amt)) || 0, // dollars with 2 decimals
        chargeitemid: li.chargeitemid ? Number(li.chargeitemid) : 0,
        chargename: li.chargename || '',
        description: li.description || null,
        warehouseid: li.warehouseid ? Number(li.warehouseid) : null,
      })),
  });

  // Remove association helper
  const removeAssociation = (type, idVal) => {
    if (type === 'receipt') setAssocReceipts(prev => (prev || []).filter(n => n !== idVal));
    else if (type === 'order') setAssocOrders(prev => (prev || []).filter(n => n !== idVal));
    else setAssocEorders(prev => (prev || []).filter(n => n !== idVal));
  };

  const isAssocEnabled = !!clientid;

  // Fetch list for current type + client (structured rows for table rendering)
  useEffect(() => {
    let to = null;
    const run = async () => {
      if (!isAssocEnabled) { setAssocRows([]); return; }
      setAssocLoading(true);
      try {
        const q = assocSearch ? `&q=${encodeURIComponent(assocSearch)}` : '';
        if (assocType === 'receipt') {
          const res = await fetch(`${API}/api/receipts?clientid=${clientid}&deleted=0&limit=200&sort=date:DESC${q}`);
          const j = await res.json();
          const rows = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
          setAssocRows(rows.map(r => ({
            id: Number(r.receiptid),
            number: Number(r.receiptid),
            date: r.date || null,
            status: r.statusname || '',
            extra: r.warehousename || '',
          })));
        } else if (assocType === 'order') {
          const res = await fetch(`${API}/api/orders?clientid=${clientid}&deleted=0&limit=200&sortField=orderdate&sortOrder=DESC${q}`);
          const j = await res.json();
          const rows = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
          setAssocRows(rows.map(r => ({
            id: Number(r.id),
            number: Number(r.id),
            date: r.orderdate || r.datecreated || null,
            status: r.statusname || '',
            extra: r.warehousename || '',
          })));
        } else {
          const res = await fetch(`${API}/api/eorders?clientid=${clientid}&limit=200&sortField=orderdate&sortOrder=DESC${q}`);
          const j = await res.json();
          const rows = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
          setAssocRows(rows.map(r => ({
            id: Number(r.id),
            number: r.ordernumber || Number(r.id),
            date: r.orderdate || null,
            status: r.ourstatusname || '',
            extra: '',
          })));
        }
      } catch {
        setAssocRows([]);
      } finally {
        setAssocLoading(false);
      }
    };
    // debounce
    to = setTimeout(run, 250);
    return () => { if (to) clearTimeout(to); };
  }, [API, clientid, assocType, assocSearch, isAssocEnabled]);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit ? `${API}/api/invoices/${invoiceId}` : `${API}/api/invoices`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadFromState()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save invoice');
      }
      const json = await res.json();
      const newId = json.id || invoiceId;
      navigate(`/invoice/${newId}`);
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    // Always return to the invoices list when cancelling
    navigate('/invoices');
  };

  if (loading) {
    return <div className="invoice-editor-wrapper">Loading…</div>;
  }

  const visibleSelectedForType = () => {
    if (assocType === 'receipt') return assocReceipts;
    if (assocType === 'order') return assocOrders;
    return assocEorders;
  };

  const toggleCheck = (id, checked) => {
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) return;
    if (assocType === 'receipt') setAssocReceipts(prev => checked ? Array.from(new Set([...(prev||[]), n])) : (prev||[]).filter(x=>x!==n));
    else if (assocType === 'order') setAssocOrders(prev => checked ? Array.from(new Set([...(prev||[]), n])) : (prev||[]).filter(x=>x!==n));
    else setAssocEorders(prev => checked ? Array.from(new Set([...(prev||[]), n])) : (prev||[]).filter(x=>x!==n));
  };

  const selectAllVisible = () => {
    const ids = assocRows.map(r => r.id).filter(n => Number.isFinite(n) && n > 0);
    if (!ids.length) return;
    if (assocType === 'receipt') setAssocReceipts(prev => Array.from(new Set([...(prev||[]), ...ids])));
    else if (assocType === 'order') setAssocOrders(prev => Array.from(new Set([...(prev||[]), ...ids])));
    else setAssocEorders(prev => Array.from(new Set([...(prev||[]), ...ids])));
  };

  const clearVisible = () => {
    const ids = new Set(assocRows.map(r => r.id));
    if (assocType === 'receipt') setAssocReceipts(prev => (prev||[]).filter(n => !ids.has(n)));
    else if (assocType === 'order') setAssocOrders(prev => (prev||[]).filter(n => !ids.has(n)));
    else setAssocEorders(prev => (prev||[]).filter(n => !ids.has(n)));
  };

  return (
    <div className="invoice-editor-wrapper">
      <div className="editor-card">
        <div className="editor-header">
          <h1>{isEdit ? `Edit Invoice #${invoiceId}` : 'New Invoice'}</h1>
          <div className="editor-actions">
            <button className="btn" onClick={save} disabled={!canSave || saving}>
              <FaSave size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={cancel}>
              <FaTimes size={14} /> Cancel
            </button>
          </div>
        </div>

        <fieldset className="editor-fieldset">
          <legend>Invoice Details</legend>
          <div className="editor-grid">
            <label>
              <span>Client</span>
              <select value={clientid} onChange={(e) => setClientid(e.target.value)}>
                <option value="">-- Select client --</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name} (#{c.id})</option>
                ))}
              </select>
            </label>

            <label>
              <span>Date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>

            <label className="col-span-2">
              <span>Notes</span>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="editor-fieldset" style={!isAssocEnabled ? { opacity: .6, pointerEvents: 'none' } : undefined}>
          <legend>Associate With</legend>
          {!isAssocEnabled && (
            <div style={{ marginBottom: 8, opacity: .8 }}>Select a client to enable associations.</div>
          )}
          <div className="editor-grid">
            <label>
              <span>Type</span>
              <select value={assocType} onChange={(e) => setAssocType(e.target.value)}>
                <option value="receipt">Warehouse Receipt</option>
                <option value="order">Order</option>
                <option value="eorder">eOrder</option>
              </select>
            </label>
            <label>
              <span>Search</span>
              <input
                type="text"
                value={assocSearch}
                onChange={(e) => setAssocSearch(e.target.value)}
                placeholder="Filter by number, client, status…"
              />
            </label>

            <div className="col-span-2">
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: 6 }}>
                <button type="button" className="btn" onClick={selectAllVisible} disabled={!assocRows.length}>Select All</button>
                <button type="button" className="btn" onClick={clearVisible} disabled={!assocRows.length}>Clear</button>
                {assocLoading && <span style={{ fontStyle:'italic', opacity:.7 }}>Loading…</span>}
              </div>
              <div className="assoc-list" style={{ maxHeight: 240, overflowY:'auto', border:'1px solid #444', borderRadius:4 }}>
                {assocRows.length === 0 ? (
                  <div style={{ padding: 8, opacity:.7 }}>No results</div>
                ) : (
                  <table className="assoc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 34 }}></th>
                        <th>{assocType === 'eorder' ? 'eOrder #' : (assocType === 'order' ? 'Order #' : 'WR #')}</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>{assocType === 'receipt' ? 'Warehouse' : 'Details'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assocRows.map(r => {
                        const sel = visibleSelectedForType().includes(r.id);
                        const dateText = r.date ? (() => { try { return new Date(r.date).toLocaleDateString(); } catch { return String(r.date); } })() : '';
                        return (
                          <tr key={`${assocType}-${r.id}`} className={sel ? 'selected' : ''}>
                            <td className="sel"><input type="checkbox" checked={sel} onChange={(e) => toggleCheck(r.id, e.target.checked)} /></td>
                            <td>{r.number}</td>
                            <td>{dateText}</td>
                            <td>{r.status}</td>
                            <td>{r.extra || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="col-span-2">
              {(assocReceipts.length + assocOrders.length + assocEorders.length === 0) ? (
                <div style={{ opacity: .7 }}>No associations yet.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {assocReceipts.map(id => (
                    <span key={`wr-${id}`} className="chip">
                      WR #{id}
                      <button type="button" className="chip-close" title="Remove" onClick={() => removeAssociation('receipt', id)}>×</button>
                    </span>
                  ))}
                  {assocOrders.map(id => (
                    <span key={`ord-${id}`} className="chip">
                      Order #{id}
                      <button type="button" className="chip-close" title="Remove" onClick={() => removeAssociation('order', id)}>×</button>
                    </span>
                  ))}
                  {assocEorders.map(id => (
                    <span key={`eo-${id}`} className="chip">
                      eOrder #{id}
                      <button type="button" className="chip-close" title="Remove" onClick={() => removeAssociation('eorder', id)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </fieldset>

        <fieldset className="editor-fieldset">
          <legend>Line Items</legend>
          <div className="editor-table-wrapper">
            <table className="editor-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 220 }}>Charge Name</th>
                  <th style={{ minWidth: 260 }}>Description</th>
                  <th style={{ width: 90 }}>Qty</th>
                  <th style={{ width: 120 }}>Rate</th>
                  <th style={{ width: 120 }}>Extended</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((li, idx) => {
                  const qty = Number(li.qty) || 0;
                  const amt = Number(li.amt) || 0;
                  const ext = round2(qty * amt);

                  return (
                    <tr key={idx}>
                      <td>
                        <input
                          className="li-input"
                          type="text"
                          value={li.chargename}
                          onChange={(e) => updateItem(idx, 'chargename', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="li-input"
                          type="text"
                          value={li.description}
                          onChange={(e) => updateItem(idx, 'description', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="li-input qty-input"
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={li.qty}
                          onChange={(e) => updateItem(idx, 'qty', toDollarInput(e.target.value))}
                          onBlur={(e) => updateItem(idx, 'qty', toPretty2(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          className="li-input rate-input"
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={li.amt}
                          onChange={(e) => updateItem(idx, 'amt', toDollarInput(e.target.value))}
                          onBlur={(e) => updateItem(idx, 'amt', toPretty2(e.target.value))}
                        />
                      </td>
                      <td className="extended-cell">{money(ext)}</td>
                      <td>
                        <button
                          type="button"
                          className="mini-btn danger"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                          title="Remove line"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="editor-lineitems-actions">
            <button type="button" className="btn" onClick={addItem}>
              + Add Line
            </button>
          </div>

          {/* Running total due below line items */}
          <div className="total-due">
            <span>Total Due:</span>
            <span className="value">{money(total)}</span>
          </div>
        </fieldset>

        <fieldset className="editor-fieldset">
          <legend>Taxes & Discounts</legend>
          <div className="adjustments-grid">
            <label>
              <span>Tax</span>
              <div className="inline">
                <select value={taxMode} onChange={(e) => setTaxMode(e.target.value)}>
                  <option value="percent">%</option>
                  <option value="fixed">$</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  value={taxValue}
                  onChange={(e) => setTaxValue(e.target.value)}
                />
              </div>
            </label>

            <label>
              <span>Discount</span>
              <div className="inline">
                <select value={discountMode} onChange={(e) => setDiscountMode(e.target.value)}>
                  <option value="fixed">$</option>
                  <option value="percent">%</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              </div>
            </label>
          </div>

          <div className="totals-box">
            <div><strong>Sub-Total:</strong> {money(subTotal)}</div>
            <div><strong>Tax:</strong> {money(taxAmount)}</div>
            <div><strong>Discount:</strong> -{money(discountAmount)}</div>
            <div className="grand-total"><strong>Total:</strong> {money(total)}</div>
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default InvoiceEditor;
